/**
 * Renders the row of SSO provider buttons (Google, Microsoft, Apple, ...).
 *
 * Used by LoginPage and RegisterPage so that adding a new SSO provider only
 * requires updating `ssoProviders.ts` — the buttons appear automatically.
 */
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';

import { getSortedProviders, type SSOProviderId } from './ssoProviders';

interface SSOButtonListProps {
  loadingProvider: SSOProviderId | null;
  isAnyLoading: boolean;
  onSelect: (provider: SSOProviderId, displayName: string) => void;
  /** i18n key for the button label template. Defaults to 'sso.signInWith'. */
  labelKey?: 'sso.signInWith' | 'sso.signUpWith';
  /** Restrict to a subset of providers (e.g. the external portal offers only
   * Google/Microsoft — Apple's form_post flow isn't wired for the portal). */
  providerIds?: SSOProviderId[];
}

export function SSOButtonList({ loadingProvider, isAnyLoading, onSelect, labelKey = 'sso.signInWith', providerIds }: SSOButtonListProps) {
  const { t } = useTranslation('auth');
  const allProviders = getSortedProviders();
  const providers = providerIds ? allProviders.filter((p) => providerIds.includes(p.id)) : allProviders;

  return (
    <div className="space-y-2 mb-4">
      {providers.map(({ id, displayName, Icon }) => (
        <Button
          key={id}
          type="button"
          variant="outline"
          className="w-full"
          size="lg"
          onClick={() => onSelect(id, displayName)}
          disabled={isAnyLoading}
        >
          {loadingProvider === id ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : (
            <Icon className="mr-2 h-5 w-5" />
          )}
          {t(labelKey, { provider: displayName })}
        </Button>
      ))}
    </div>
  );
}
